/**
 * Cliente de Supabase
 * 
 * Configuración del cliente Supabase para el sistema de agendamiento de citas.
 * Este archivo maneja la conexión con la base de datos PostgreSQL de Supabase.
 * 
 * @author Conectemos Chile
 */

import { createClient } from '@supabase/supabase-js';
// import { Database } from '../../types/database.types';

// ============================================================================
// CONFIGURACIÓN DE SUPABASE
// ============================================================================

// Fallbacks seguros: permiten que la app cargue en modo degradado si faltan las vars.
// Las queries fallarán en red (manejado graciosamente), pero no crashea la app.
export const supabaseUrl = process.env.REACT_APP_SUPABASE_URL?.trim() || 'https://placeholder.supabase.co';
export const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY?.trim() || 'placeholder-key-not-configured';

if (supabaseUrl === 'https://placeholder.supabase.co' || supabaseAnonKey === 'placeholder-key-not-configured') {
  console.error(
    '[Supabase] Variables de entorno no configuradas. ' +
    'La app funciona en modo degradado — configurá REACT_APP_SUPABASE_URL y REACT_APP_SUPABASE_ANON_KEY en Vercel.'
  );
}

// ============================================================================
// CLIENTE SUPABASE
// ============================================================================

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
});

// ============================================================================
// TIPOS DE CLIENTE
// ============================================================================

export type SupabaseClient = typeof supabase;

/**
 * Tipo para respuestas de Supabase con manejo de errores
 */
export interface SupabaseResponse<T> {
  data: T | null;
  error: Error | null;
  success: boolean;
}

/**
 * Helper para manejar respuestas de Supabase de forma consistente
 */
export function handleSupabaseResponse<T>(
  response: { data: T | null; error: any }
): SupabaseResponse<T> {
  return {
    data: response.data,
    error: response.error,
    success: !response.error && response.data !== null,
  };
}

// ============================================================================
// VALIDACIÓN DE CONEXIÓN
// ============================================================================

/**
 * Verifica que la conexión con Supabase esté funcionando
 */
export async function testSupabaseConnection(): Promise<boolean> {
  try {
    const { error } = await supabase.from('terapeutas').select('count').limit(1);
    return !error;
  } catch (error) {
    console.error('Error conectando con Supabase:', error);
    return false;
  }
}

// ============================================================================
// EXPORTACIONES
// ============================================================================

export default supabase;